import React, { FunctionComponent, useEffect, useRef } from 'react';
import { Button } from '../../../ui';
import { useAccount } from '../../../../hooks/useAccount';
import { useBriefMutations } from '../../../../hooks/useBriefs';
import { Brief, getKeywordsByType } from '../../../../services/models/brief';
import { BriefKeywordType } from '../../../../services/models/brief-keyword-type';
import EditBriefExclusions from '../../../edit-brief-exclusions/EditBriefExclusions';
import EditBriefFilters from '../../../edit-brief-filters/EditBriefFilters';
import EditBriefTopics from '../../../edit-brief-topics/EditBriefTopics';

interface Props {
  active: boolean;
  draftBrief: Brief | null;
  refreshPreview: (briefId: number) => void;
  onComplete: () => void;
}

const EditSidebar: FunctionComponent<Props> = ({
  active,
  draftBrief,
  refreshPreview,
  onComplete,
}) => {
  const { createBriefKeyword, destroyBriefKeyword } = useBriefMutations();

  const getStoriesPreview = refreshPreview;
  const createTopic = (briefId: number, keyword: string) =>
    createBriefKeyword(briefId, keyword, BriefKeywordType.Topic);
  const createFilter = (briefId: number, keyword: string) =>
    createBriefKeyword(briefId, keyword, BriefKeywordType.Filter);
  const createExclusion = (briefId: number, keyword: string) =>
    createBriefKeyword(briefId, keyword, BriefKeywordType.Exclusion);
  const destroyTopic = (briefId: number, id: number) => destroyBriefKeyword(briefId, id);
  const destroyFilter = (briefId: number, id: number) => destroyBriefKeyword(briefId, id);
  const destroyExclusion = (briefId: number, id: number) => destroyBriefKeyword(briefId, id);
  const { account: currentAccount } = useAccount();
  const account = currentAccount!;
  const changesHashRef = useRef<string>('');

  const onCreateTopic = (keyword: string) => {
    if (!draftBrief) {
      return;
    }

    const matchingExclusion = (
      getKeywordsByType(draftBrief, BriefKeywordType.Exclusion) || []
    ).find(x => x.keyword?.toLowerCase() === keyword.toLocaleLowerCase());

    if (matchingExclusion && matchingExclusion.id) {
      destroyExclusion(draftBrief.id, matchingExclusion.id);
    }

    createTopic(draftBrief.id, keyword);
  };

  const onCreateExclusion = (keyword: string) => {
    if (!draftBrief) {
      return;
    }

    const mathingTopic = (getKeywordsByType(draftBrief, BriefKeywordType.Topic) || []).find(
      x => x.keyword?.toLowerCase() === keyword.toLocaleLowerCase()
    );

    if (mathingTopic && mathingTopic.id) {
      destroyTopic(draftBrief.id, mathingTopic.id);
    }

    createExclusion(draftBrief.id, keyword);
  };

  useEffect(() => {
    if (draftBrief && !draftBrief.isFetching) {
      const changesHash = [...draftBrief.keywords.map(x => x.keyword)].join(', ');

      if (changesHashRef.current !== changesHash) {
        getStoriesPreview(draftBrief.id);
        changesHashRef.current = changesHash;
      }
    }
  }, [draftBrief, getStoriesPreview]);

  if (!active || !draftBrief) {
    return <></>;
  }

  return (
    <div className="fixed right-0 z-[1040] w-[296px] overflow-y-auto border-l border-gray-200 bg-white p-4 [&>*]:pb-2.5 max-sm:w-[250px]">
      <div className="text-right">
        <Button variant="purple" onClick={() => onComplete()}>
          Next
        </Button>
      </div>
      <EditBriefTopics
        maxItems={account.product.features.keywordsPerBrief}
        brief={draftBrief}
        create={k => onCreateTopic(k)}
        destroy={id => destroyTopic(draftBrief.id, id)}
      />
      <EditBriefFilters
        maxItems={account.product.features.keywordsPerBrief}
        brief={draftBrief}
        create={k => createFilter(draftBrief.id, k)}
        destroy={id => destroyFilter(draftBrief.id, id)}
      />
      <EditBriefExclusions
        maxItems={account.product.features.keywordsPerBrief}
        brief={draftBrief}
        create={k => onCreateExclusion(k)}
        destroy={id => destroyExclusion(draftBrief.id, id)}
      />
    </div>
  );
};

export default EditSidebar;
